import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiCheckbox,
  EuiLoadingSpinner,
  EuiSelect,
} from "@elastic/eui";
import { AlertType } from "../../profileTypes";
import { useNewProfileAlertsListStyles } from "../../profileStyles";
import IconPopover from "../../../../../../components/Popover/IconPopover";
import FdDataTableRowActions from "../../../../../../components/DataTable/FdDataTableRowActions";
import { AlertTypeDetailsFlyout } from "pages/AdminConsole/Monitoring/AlertTypes/AlertTypeDetailsFlyout";
import { isNullOrUndefined } from "utils";
import BadgeCount from "components/Badge/BadgeCount";

interface Props {
  selectedAlerts: AlertType[];
  setSelectedAlerts: (alerts: AlertType[]) => void;
  setIsDirty: (isDirty: boolean) => void;
  availableAlertTypes: AlertType[];
  isSystemProfile?: boolean;
}

const getItemId = (alert: AlertType) =>
  `${alert.id}-${
    alert.subtypeId !== undefined && alert.subtypeId !== null ? alert.subtypeId.toString() : "null"
  }`;

const getSortValueInternal = (alert: AlertType, field: keyof AlertType) => {
  switch (field) {
    default:
      return alert[field];
  }
};

const ProfileAlertsList = ({
  selectedAlerts,
  setSelectedAlerts,
  setIsDirty,
  availableAlertTypes,
  isSystemProfile,
}: Props) => {
  const classes = useNewProfileAlertsListStyles();
  const [sortField, setSortField] = useState<keyof AlertType>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const tableKey = useRef(0);
  const [selectedItem, setSelectedItem] = useState<AlertType | null>(null);

  const getSortValue = useCallback((alert: AlertType, field: keyof AlertType) => {
    return getSortValueInternal(alert, field);
  }, []);

  const onClose = () => {
    setSelectedItem(null);
  };

  useEffect(() => {
    tableKey.current += 1;
  }, [selectedAlerts]);

  useEffect(() => {
    if (selectedAlerts && selectedAlerts.length > 0) {
      const sorted = [...selectedAlerts].sort((a, b) => {
        const aValue = getSortValue(a, sortField);
        const bValue = getSortValue(b, sortField);
        const modifier = sortDirection === "asc" ? 1 : -1;

        if (aValue === bValue) return 0;
        if (aValue === undefined || aValue === null) return sortDirection === "asc" ? -1 : 1;
        if (bValue === undefined || bValue === null) return sortDirection === "asc" ? 1 : -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return aValue.localeCompare(bValue) * modifier;
        }
        // @ts-ignore
        return (aValue < bValue ? -1 : 1) * modifier;
      });

      let orderChanged = selectedAlerts.length !== sorted.length;
      if (!orderChanged) {
        for (let i = 0; i < selectedAlerts.length; i++) {
          if (getItemId(selectedAlerts[i]) !== getItemId(sorted[i])) {
            orderChanged = true;
            break;
          }
        }
      }

      if (orderChanged) {
        setSelectedAlerts(sorted);
      }
    }
  }, [selectedAlerts, sortField, sortDirection, setSelectedAlerts, getSortValue]);

  const handleChangeSeverity = (
    e: React.ChangeEvent<HTMLSelectElement>,
    alertId: number,
    subtypeId: number | null
  ) => {
    if (isSystemProfile) return;

    const newSeverity = parseInt(e.target.value) as 1 | 3 | 5;

    const updatedAlerts = selectedAlerts.map((alert) => {
      if (alert.id === alertId && alert.subtypeId === subtypeId) {
        return {
          ...alert,
          severityTypeId: newSeverity,
        } as AlertType;
      }
      return alert;
    });

    setSelectedAlerts(updatedAlerts);
  };

  const handleChangeTolerance = (
    e: React.ChangeEvent<HTMLSelectElement>,
    alertId: number,
    subtypeId: number | null
  ) => {
    if (isSystemProfile) return;

    const newThreshold = parseInt(e.target.value);

    const updatedAlerts = selectedAlerts.map((alert) => {
      if (alert.id === alertId && alert.subtypeId === subtypeId) {
        return {
          ...alert,
          thresholdId: newThreshold,
        };
      }
      return { ...alert };
    });

    setSelectedAlerts(updatedAlerts);
    setIsDirty(true);
  };

  const processFormula = (alertType: AlertType): string => {
    const matchingAvailableAlert = availableAlertTypes.find(
      (available) =>
        available.id === alertType.id &&
        (available.subtypeId === alertType.subtypeId ||
          (!available.subtypeId && !alertType.subtypeId))
    );

    if (matchingAvailableAlert === undefined || matchingAvailableAlert.formula == null) {
      return "No Formula Defined";
    }

    if (!matchingAvailableAlert.thresholds?.length || !alertType.thresholdId) {
      return matchingAvailableAlert.formula;
    }

    const selectedThresholdDefinition = matchingAvailableAlert.thresholds.find(
      (th) => th.id === alertType.thresholdId
    );

    if (!selectedThresholdDefinition) {
      return matchingAvailableAlert.formula;
    }

    let processedFormula = matchingAvailableAlert.formula;

    selectedThresholdDefinition.values.forEach(({ key, value }) => {
      const placeholder = `$${key}`;
      processedFormula = processedFormula.replace(new RegExp("\\" + placeholder, "g"), value);
    });

    return processedFormula;
  };

  const getToleranceOptions = (alert: AlertType) => {
    const matchingAvailableAlert = availableAlertTypes.find(
      (available) =>
        available.id === alert.id &&
        (available.subtypeId === alert.subtypeId || (!available.subtypeId && !alert.subtypeId))
    );

    if (!matchingAvailableAlert?.thresholds) {
      return [];
    }

    const predefinedOrder = ["Low", "Medium", "High"];

    const sortedThresholds = [...matchingAvailableAlert.thresholds].sort((a, b) => {
      const aIndex = predefinedOrder.indexOf(a.name);
      const bIndex = predefinedOrder.indexOf(b.name);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }
      return a.name.localeCompare(b.name);
    });

    return (
      sortedThresholds.map((threshold) => ({
        value: threshold.id,
        text: threshold.name,
      })) || []
    );
  };

  const tableHeight = selectedAlerts.length > 8 ? "500px" : "auto";

  const rowActions = {
    buttons: [
      {
        name: "View Alert Type",
        icon: "eye",
        onClick: (row: any) => {
          setSelectedItem(row);
        },
      },
      {
        name: "Delete",
        icon: "trash",
        onClick: (row: AlertType) => {
          const updatedAlerts = selectedAlerts.filter(
            (alert) => getItemId(alert) !== getItemId(row)
          );
          setSelectedAlerts(updatedAlerts);
          setIsDirty(true);
        },
        buttonProps: {
          color: "danger",
        },
      },
    ],
    title: "Actions",
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSystemProfile) return;

    const isChecked = e.target.checked;
    const updatedAlerts = selectedAlerts.map((alert) => ({
      ...alert,
      tableChecked: isChecked ? "on" : undefined,
    }));

    // @ts-ignore
    setSelectedAlerts(updatedAlerts);
    setIsDirty(true);
  };

  const AlertHeader = () => {
    if (selectedAlerts.length === 0) {
      return <span style={{ fontWeight: 400 }}>Alert Type</span>;
    } else {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontWeight: 600 }}>Alert Types</span>{" "}
          <BadgeCount number={selectedAlerts?.length} />
        </div>
      );
    }
  };

  const columns: Array<EuiBasicTableColumn<AlertType>> = [
    {
      name: (
        <EuiCheckbox
          id="select-all-alerts-checkbox"
          checked={
            selectedAlerts.length > 0 &&
            selectedAlerts.every((alert) => alert.tableChecked === "on")
          }
          disabled={isSystemProfile}
          onChange={handleSelectAllChange}
          aria-label="Select all alerts"
        />
      ),
      width: "50px",
      align: "center",
      render: (alert: AlertType) => (
        <EuiCheckbox
          id={`table_checkbox_${alert.id}-${alert.subtypeId ?? "null"}`}
          checked={alert.tableChecked === "on"}
          disabled={isSystemProfile}
          onChange={(e) => {
            if (isSystemProfile) return;

            const updatedAlerts = selectedAlerts.map((a) =>
              a.id === alert.id && a.subtypeId === alert.subtypeId
                ? { ...a, tableChecked: e.target.checked ? "on" : undefined }
                : a
            );
            // @ts-ignore
            setSelectedAlerts(updatedAlerts);
            setIsDirty(true);
          }}
        />
      ),
    },
    {
      field: "name",
      name: <AlertHeader />,
      sortable: true,
      truncateText: false,
    },
    {
      field: "severity",
      name: "Severity",
      width: "150px",
      render: (_, alert: AlertType) => {
        return (
          <EuiSelect
            style={{ backgroundColor: "var(--card-background)" }}
            key={`select-${alert.id}-${alert.severityTypeId}`}
            options={[
              { value: 1, text: "Informational" },
              { value: 3, text: "Warning" },
              { value: 5, text: "Critical" },
            ]}
            value={Number(alert.severityTypeId)}
            disabled={isSystemProfile}
            onChange={(e) => {
              handleChangeSeverity(e, alert.id, alert.subtypeId ?? null);
              setIsDirty(true);
            }}
          />
        );
      },
      sortable: false,
    },
    {
      field: "tolerance",
      name: "Tolerance",
      width: "150px",
      render: (_, alert: AlertType) => {
        const toleranceOptions = getToleranceOptions(alert);

        return toleranceOptions.length ? (
          <EuiSelect
            style={{ backgroundColor: "var(--card-background)" }}
            key={`select-${alert.id}-${alert.thresholdId}`}
            options={toleranceOptions}
            value={Number(alert.thresholdId)}
            disabled={isSystemProfile}
            onChange={(e) => {
              handleChangeTolerance(e, alert.id, alert.subtypeId ?? null);
              setIsDirty(true);
            }}
          />
        ) : (
          "Not Applicable"
        );
      },
      sortable: false,
    },
    {
      field: "formula",
      name: (
        <>
          Tolerance
          <br />
          Formula
        </>
      ),
      align: "center",
      sortable: false,
      render: (formula: string, alert: AlertType) => {
        if (!availableAlertTypes.length) {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <EuiLoadingSpinner size="m" />
            </div>
          );
        }
        const processedFormula = processFormula(alert) || "No Formula Defined";
        return (
          <IconPopover
            icon="controlsHorizontal"
            iconColor="var(--violet)"
            items={[{ value: processedFormula }]}
            showCount={false}
            textSize="14px"
            copy={true}
          />
        );
      },
      width: "100px",
    },
    {
      name: "",
      align: "center",
      width: "60px",
      render: (alert: AlertType) => {
        return (
          <FdDataTableRowActions
            // @ts-ignore
            Buttons={rowActions.buttons}
            Row={alert}
            Title={rowActions.title}
          />
        );
      },
    },
  ];

  return (
    <div
      className={classes.tableWrapper}
      style={{
        maxHeight: tableHeight,
        overflow: selectedAlerts.length > 8 ? "auto" : "visible",
      }}>
      <EuiBasicTable
        key={tableKey.current}
        items={selectedAlerts}
        itemId={getItemId}
        columns={columns}
        rowHeader="name"
        className={classes.table}
        noItemsMessage="No alerts assigned"
        sorting={{
          sort: {
            field: sortField,
            direction: sortDirection,
          },
        }}
        onChange={({ sort }) => {
          if (sort) {
            setSortField(sort.field as keyof AlertType);
            setSortDirection(sort.direction);
          }
        }}
      />
      {!isNullOrUndefined(selectedItem) && (
        <AlertTypeDetailsFlyout onClose={onClose} selectedItem={selectedItem} canEdit={false} />
      )}
    </div>
  );
};

export default ProfileAlertsList;
